import React, { ReactNode, useCallback } from "react";
import { useNavigate } from "react-router";

import { Title } from "capitalrx-components-2.0/lib/typography";
import { Button, ButtonProps, Panel, useSearchFormRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Form } from "capitalrx-components-2.0/lib/forms";
import { useLoosePermissionCheck, useModulePathResolver } from "capitalrx-shell";


import { pricingLevelCreate } from "@/App/constants/permissions";
import { PricingLevelsTypes } from "@/types";
import { getApiBaseUrl } from "../../api";

import { PricingLevelListTable } from "./PricingLevelListTable";
import { queryString } from "capitalrx-utilities";
import { PricingLevelFilterForm } from "./PricingLevelFilterForm";


export interface PricingLevelFilterFormType {
  lines_of_business?: Array<string>;
  external_account_id?: string;
  external_group_id?: string;
  coverage_strategy_ids?: Array<string>;
  external_network_ids?: Array<string>;
  client_ids?: string | Array<string>;
  agreement_types?: Array<string>;
  agreement_channels?: Array<string>;
  notes?: string;
}


const PricingLevels: React.FC = () => {
  const navigate = useNavigate();
  const modulePath = useModulePathResolver();

  const { isMobile } = useResponsive({
    isMobile: { desktop: false, mobile: true },
  });

  const permittedFilterKeys = [
    "lines_of_business",
    "external_account_ids",
    "external_group_ids",
    "coverage_strategy_ids",
    "external_network_ids",
    "client_ids",
    "agreement_types",
    "agreement_channels",
    "notes",
  ];

  const urlParams = queryString.getQueryParams(location.search, { arrayFormat: "bracket" }) as Partial<PricingLevelFilterFormType>;

  const handleSubmit = useCallback(
    async (data: Partial<PricingLevelFilterFormType>): Promise<void> => {
      const searchParams = queryString.convertJsonToQueryString(data, { arrayFormat: "bracket" });
      navigate({
        pathname: location.pathname,
        search: `?${searchParams}`,
      });
    },
    [navigate],
  );

  const canPricingLevel = useLoosePermissionCheck(pricingLevelCreate);

  interface ButtonContent extends ButtonProps {
    title: string | ReactNode;
    path?: string;
  }

  const buttonGroupContents: Array<ButtonContent> = [
    {
      title: "NEW PRICING LEVEL",
      path: "/pricing-levels/create",
      appearance: "secondary",
      icon: "add",
    },
  ];

  const dataSource = useSearchFormRemoteTableDataSource(
    `${getApiBaseUrl()}/pricing_level/list`,
    (row: PricingLevelsTypes.PricingLevelAPIResult) => String(row.pricing_level.id),
    {
      sort: [{ name: "created_at", direction: "descending" }],
    },
    permittedFilterKeys,
    { arrayFormat: "bracket" },
  );

  return (
    <Stack spacing="medium">
      <Grid>
        {isMobile && (
          <Stack spacing="medium">
            <Title>{"Pricing Levels"}</Title>
            {canPricingLevel && (
              <Stack>
                {buttonGroupContents.map(({ title, path, onClick, ...restProps }, index) => {
                  const handleGroupButtonClick = (): void => {
                    if (path) navigate(modulePath(path));
                    if (onClick) onClick();
                  };

                  return (
                    <Button key={`${index}-${title}`} {...restProps} onClick={handleGroupButtonClick}>
                      {title}
                    </Button>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}
        {!isMobile && (
          <Stack spacing="medium">
            <Shelf>
              <Title>{"Pricing Levels"}</Title>
              <Shelf.Spacer />
              {canPricingLevel && (
                <Shelf>
                  {buttonGroupContents.map(({ title, path, onClick, ...restProps }, index) => {
                    const handleGroupButtonClick = (): void => {
                      if (path) navigate(modulePath(path));
                      if (onClick) onClick();
                    };

                    return (
                      <Button key={`${index}-${title}`} {...restProps} onClick={handleGroupButtonClick}>
                        {title}
                      </Button>
                    );
                  })}
                </Shelf>
              )}
            </Shelf>
          </Stack>
        )}
      </Grid>
      <Panel>
        <Stack spacing="medium-large">
          <Form onSubmit={handleSubmit} defaultValues={urlParams}>
            <PricingLevelFilterForm submitForm={handleSubmit} />
          </Form>
          <PricingLevelListTable dataSource={dataSource} />
        </Stack>
      </Panel>
    </Stack>
  );
};

export default PricingLevels;
